import { baseApi } from "./baseApi";

const NOTE_BASE_URL = "/notes";

export const NoteApi = baseApi.injectEndpoints({
  tagTypes: ["Note"],

  endpoints: (builder) => ({
    getNotesBySource: builder.query({
      query: (params) => ({
        url: NOTE_BASE_URL,
        params, // Accepts { source: "Order", sourceRef: "id" } as query parameters
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "Note",
                id: _id,
              })),
              { type: "Note", id: "LIST" },
            ]
          : [{ type: "Note", id: "LIST" }],
    }),

    addNoteSource: builder.mutation({
      query: (payload) => ({
        url: `${NOTE_BASE_URL}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { source }) => [
        { type: "Note", id: "LIST" },
        ...(source === "VendorLeadTask"
          ? [{ type: "Note", id: "TASK_LIST" }]
          : []),
      ],
    }),

    createBulkShipmentNotes: builder.mutation({
      query: (bulkNoteData) => ({
        url: `${NOTE_BASE_URL}/bulk-shipment-note`,
        method: "POST",
        body: bulkNoteData,
      }),
      invalidatesTags: (result, error, { _id }) => [
        { type: "Note", id: "LIST" },
      ],
    }),

    updateNoteById: builder.mutation({
      query: (payload) => {
        const { noteId, ...body } = payload;

        return {
          url: `${NOTE_BASE_URL}/${noteId}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: (result, error, { source }) => [
        { type: "Note", id: "LIST" },
        ...(source === "VendorLeadTask"
          ? [{ type: "Note", id: "TASK_LIST" }]
          : []),
      ],
    }),

    addReplyToNote: builder.mutation({
      query: ({ noteId, ...payload }) => ({
        url: `${NOTE_BASE_URL}/${noteId}/replies`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { noteId }) => [
        { type: "Note", id: noteId },
        { type: "Note", id: "LIST" },
        { type: "Note", id: "TASK_LIST" },
      ],
    }),

    updateReplyInNote: builder.mutation({
      query: ({ noteId, replyId, ...payload }) => ({
        url: `${NOTE_BASE_URL}/${noteId}/replies/${replyId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: (result, error, { noteId, replyId }) => [
        { type: "Note", id: noteId },
        { type: "Reply", id: replyId },
        { type: "Note", id: "LIST" },
        { type: "Note", id: "TASK_LIST" },
      ],
    }),

    getFilteredNotesBySource: builder.query({
      query: (params) => ({
        url: `${NOTE_BASE_URL}/notelist`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "Note",
                id: _id,
              })),
              { type: "Note", id: "LIST" },
            ]
          : [{ type: "Note", id: "LIST" }],
    }),

    getNoteVendorLeadTaskBySource: builder.query({
      query: ({ sourceRef }) => ({
        url: `${NOTE_BASE_URL}/tasks`,
        params: { sourceRef },
      }),
      providesTags: (result) =>
        Array.isArray(result) && result.length
          ? [
              ...result.map(({ _id }) => ({ type: "Note", id: _id })),
              { type: "Note", id: "TASK_LIST" },
            ]
          : [{ type: "Note", id: "TASK_LIST" }],
    }),

    getVendorLeadNoteList: builder.query({
      query: (params) => ({
        url: `${NOTE_BASE_URL}/vendor-lead-notes`,
        params,
      }),
    }),
    getVendorLeadTaskList: builder.query({
      query: (params) => ({
        url: `${NOTE_BASE_URL}/vendor-lead-tasks`,
        params,
      }),
    }),
    getMyVendorLeadTasks: builder.query({
      query: () => ({
        url: `${NOTE_BASE_URL}/my-tasks`,
      }),
      providesTags: [{ type: "Note", id: "MY_TASKS" }],
    }),
  }),
});

export const {
  useGetNotesBySourceQuery,
  useGetFilteredNotesBySourceQuery,
  useLazyGetFilteredNotesBySourceQuery,
  useAddNoteSourceMutation,
  useCreateBulkShipmentNotesMutation,
  useUpdateNoteByIdMutation,
  useAddReplyToNoteMutation,
  useUpdateReplyInNoteMutation,
  useGetNoteVendorLeadTaskBySourceQuery,
  useLazyGetVendorLeadNoteListQuery,
  useLazyGetVendorLeadTaskListQuery,
  useLazyGetMyVendorLeadTasksQuery,
} = NoteApi;
